import React, {useEffect, useState} from 'react';
import {
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Grid,
  Link,
  Typography,
} from '@material-ui/core';
import {InfoCard} from '@backstage/core-components';
import {useApi} from '@backstage/core-plugin-api';
import {utilitiesApiRef} from '../../../../scaffolder/utils/bitbucketUtilitiesApi';
import {slugFromRepoUrl} from '../generateCatalogInfo';

type OpenPrStepProps = {
  repoUrl: string;
  yaml: string;
  registeredBy: string;
  hasPermission: boolean;
  mainBranch: string;
  onBack: () => void;
  onNext: (mainBranch: string) => void;
};

type PrResult = {id: number; url: string; branch: string};

/**
 * Opens the pull request that adds catalog-info.yaml to the target repo,
 * reusing the same bitbucket-utilities-backend service (and Bitbucket
 * credentials) that back the repo-exists and permission checks earlier in
 * the wizard. Users without permission to open a PR fall back to a manual
 * copy/paste-and-push walkthrough instead.
 */
export const OpenPrStep = ({
  repoUrl,
  yaml,
  registeredBy,
  hasPermission,
  mainBranch,
  onBack,
  onNext,
}: OpenPrStepProps) => {
  const utilitiesApi = useApi(utilitiesApiRef);
  const [pr, setPr] = useState<PrResult>();
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string>();
  const [copied, setCopied] = useState(false);
  const [pushConfirmed, setPushConfirmed] = useState(false);

  const createPr = async () => {
    setCreating(true);
    setError(undefined);
    try {
      const slug = slugFromRepoUrl(repoUrl);
      const result = await utilitiesApi.createCatalogInfoPr(slug, yaml, registeredBy);
      setPr(result);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  useEffect(() => {
    if (hasPermission) {
      createPr();
    }
    // Only run once when this step is first shown.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasPermission]);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(yaml);
    setCopied(true);
  };

  if (!hasPermission) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <InfoCard title='Add catalog-info.yaml by hand'>
            <Typography variant='body1' paragraph>
              You don't currently have permission to open a pull request on this repo
              automatically, so here's how to add it yourself:
            </Typography>
            <Typography component='ol' variant='body1'>
              <li>
                Copy the catalog-info.yaml generated in the previous step using the button below.
              </li>
              <li>
                Add it as <strong>catalog-info.yaml</strong> at the root of{' '}
                <strong>{repoUrl}</strong>.
              </li>
              <li>
                Push (or merge a pull request you open by hand) directly to the{' '}
                <strong>{mainBranch}</strong> branch.
              </li>
            </Typography>
            <Button onClick={handleCopy} variant='outlined' style={{marginTop: 8}}>
              {copied ? 'Copied!' : 'Copy catalog-info.yaml'}
            </Button>
          </InfoCard>
        </Grid>
        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Checkbox
                checked={pushConfirmed}
                onChange={e => setPushConfirmed(e.target.checked)}
              />
            }
            label={
              <>
                I've pushed catalog-info.yaml to the <strong>{mainBranch}</strong> branch of{' '}
                {repoUrl}
              </>
            }
          />
        </Grid>
        <Grid item xs={12}>
          <Button onClick={onBack}>Back</Button>
          <Button
            variant='contained'
            color='primary'
            onClick={() => onNext(mainBranch)}
            disabled={!pushConfirmed}
            style={{marginLeft: 8}}
          >
            Continue
          </Button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <InfoCard title='Pull request'>
          {creating && (
            <Grid container spacing={2} alignItems='center'>
              <Grid item>
                <CircularProgress size={20} />
              </Grid>
              <Grid item>
                <Typography variant='body1'>
                  Opening a pull request with catalog-info.yaml…
                </Typography>
              </Grid>
            </Grid>
          )}
          {error && (
            <>
              <Typography variant='body1' color='error' paragraph>
                Couldn't open the pull request: {error}
              </Typography>
              <Button onClick={createPr} variant='outlined'>
                Retry
              </Button>
            </>
          )}
          {pr && (
            <>
              <Typography variant='body1' paragraph>
                Opened{' '}
                <Link href={pr.url} target='_blank' rel='noopener noreferrer'>
                  pull request #{pr.id}
                </Link>{' '}
                adding catalog-info.yaml to <strong>{repoUrl}</strong>.
              </Typography>
              <Typography variant='body2' color='textSecondary' paragraph>
                Once it's merged, continue to register the component.
              </Typography>
            </>
          )}
        </InfoCard>
      </Grid>
      <Grid item xs={12}>
        <Button onClick={onBack}>Back</Button>
        <Button
          variant='contained'
          color='primary'
          onClick={() => onNext(pr!.branch)}
          disabled={!pr}
          style={{marginLeft: 8}}
        >
          I've merged catalog-info.yaml
        </Button>
      </Grid>
    </Grid>
  );
};
